class HeaderMenu extends HTMLElement {
  static observedAttributes = ['data-sticky'];
  constructor() {
    super();
    this.interactionMode = this.dataset.interactionMode;
    this.currentInteractionMode = this.dataset.interactionMode;
    this.elements = {
      menuLinks: this.querySelectorAll('a'),
      triggers: this.querySelectorAll('.header__menu-item[aria-expanded]'),
      megamenus: this.querySelectorAll('.header__primary-menu [data-submenu="megamenu"]'),
      submenus: this.querySelectorAll('.header__primary-menu [data-submenu]'),
      secondaryMenu: this.querySelector('.header__right'),
      background: this.querySelector('.header__background'),
    };
    this.returnColorScheme = [];

    this.elements.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (this.currentInteractionMode === 'hover' && Date.now() - 500 < this.timeOpened) {
          return (this.currentInteractionMode = 'click');
        } else {
          // Check if triggered via keyboard (no pointer coordinates or detail is 0 for keyboard activation)
          const isKeyboard = event.detail === 0;
          this.toggleTrigger(event, isKeyboard);
          this.currentInteractionMode = 'click';
        }
      });

      // Handle keyboard navigation on triggers
      trigger.addEventListener('keydown', (event) => {
        const triggers = Array.from(this.elements.triggers);
        const currentIndex = triggers.indexOf(trigger);

        switch (event.code) {
          case 'Enter':
          case 'Space':
            event.preventDefault();
            const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
            if (trigger.getAttribute('aria-expanded') === 'true') {
              this.closeSubmenu(submenu);
            } else {
              this.openSubmenu(submenu, true);
            }
            this.currentInteractionMode = 'click';
            break;
          case 'ArrowDown':
            event.preventDefault();
            // Open submenu and focus first item
            const submenuDown = document.getElementById(trigger.getAttribute('aria-controls'));
            if (trigger.getAttribute('aria-expanded') !== 'true') {
              this.openSubmenu(submenuDown, true);
            } else {
              const firstFocusable = submenuDown.querySelector('a, button');
              if (firstFocusable) firstFocusable.focus();
            }
            this.currentInteractionMode = 'click';
            break;
          case 'ArrowRight':
            event.preventDefault();
            // Move to next trigger
            if (currentIndex < triggers.length - 1) {
              triggers[currentIndex + 1].focus();
            } else {
              triggers[0].focus(); // Wrap to first
            }
            break;
          case 'ArrowLeft':
            event.preventDefault();
            // Move to previous trigger
            if (currentIndex > 0) {
              triggers[currentIndex - 1].focus();
            } else {
              triggers[triggers.length - 1].focus(); // Wrap to last
            }
            break;
        }
      });

      trigger.addEventListener('mouseover', (event) => {
        if (this.interactionMode === 'hover') {
          const openMenus = this.querySelectorAll('[aria-expanded="true"]');
          openMenus.forEach((openMenu) => {
            const submenu = document.getElementById(openMenu.getAttribute('aria-controls'));
            if (openMenu && !submenu.contains(event.target) && submenu !== event.target && event.target !== openMenu) {
              this.closeSubmenu(submenu, true);
            }
          });
          const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
          this.openSubmenu(submenu);
          this.currentInteractionMode = 'hover';
          document.addEventListener(
            'keydown',
            (event) => {
              if (event.code.toUpperCase() === 'ESCAPE') this.closeSubmenu(submenu);
            },
            { once: true }
          );
          this.addEventListener(
            'mouseleave',
            () => this.currentInteractionMode === 'hover' && this.closeSubmenu(submenu, true),
            { once: true }
          );
        }
      });
    });

    if (this.interactionMode === 'hover') {
      this.elements.menuLinks.forEach((trigger) => {
        trigger.addEventListener('mouseenter', (event) => {
          if (this.currentInteractionMode !== 'hover') return;
          const openMenus = this.querySelectorAll('[aria-expanded="true"]');
          openMenus.forEach((openMenu) => {
            const submenu = document.getElementById(openMenu.getAttribute('aria-controls'));
            if (openMenu && !submenu.contains(event.target) && submenu !== event.target && event.target !== openMenu) {
              this.closeSubmenu(submenu, true);
            }
          });
        });
      });
    }

    this.elements.submenus.forEach((submenu) => {
      submenu.addEventListener('keydown', (event) => this.onSubmenuKeyDown(event));
      submenu.addEventListener('focusout', (event) => this.onSubmenuFocusOut(event, submenu));
    });

    this.isProductPage = document.querySelector('.page--product');
    this.isTransparent =
      this.classList.contains('header--transparent') &&
      document.querySelector('.pdp--full[data-support-transparent-header]');

    const height = Math.floor(this.clientHeight) - 1;
    document.documentElement.style.setProperty('--header-height', height + 'px');

    this.headerObserver = new ResizeObserver((entries) => {
      if (window.matchMedia('(max-width:71.99em)').matches) {
        this.elements.submenus.forEach((submenu) => {
          this.closeSubmenu(submenu, true);
        });
      }
      if (this.isProductPage && this.isTransparent) {
        if (window.matchMedia('(min-width:48em)').matches) {
          this.classList.add('header--transparent');
        } else {
          this.classList.remove('header--transparent');
        }
      }
      const height = Math.floor(this.clientHeight) - 1;
      document.documentElement.style.setProperty('--header-height', height + 'px');
      if (this.classList.contains('header--transparent')) {
        document.documentElement.style.setProperty('--transparent-header-height', height + 'px');
      } else {
        document.documentElement.style.setProperty('--transparent-header-height', '0px');
      }
      if (this.dataset.sticky === 'always' && window.matchMedia('(min-height: 30em)').matches) {
        document.documentElement.style.setProperty('--sticky-header-height', height + 'px');
      } else {
        document.documentElement.style.setProperty('--sticky-header-height', '0px');
      }
    });
    this.headerObserver.observe(this);

    this.megamenuObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const height = entry.borderBoxSize[0].blockSize;
        if (entry.target.getAttribute('data-submenu') === 'nested-dropdown') {
          if (height > this.submenuHeight) {
            this.setSubmenuHeight(height);
          }
        } else {
          this.setSubmenuHeight(height);
        }
      }
    });

    document.addEventListener('click', (event) => {
      this.currentInteractionMode == 'click';
      const openMenus = this.querySelectorAll('[aria-expanded="true"]');
      openMenus.forEach((openMenu) => {
        const submenu = document.getElementById(openMenu.getAttribute('aria-controls'));

        if (openMenu && !submenu.contains(event.target) && submenu != event.target && event.target != openMenu) {
          this.closeSubmenu(submenu, true);
        }
      });
    });
  }

  closeSubmenu(submenu, preventFocus) {
    this.classList.remove('header--alt-logo');
    this.removeAttribute('open');
    const trigger = this.querySelector(`[aria-controls="${submenu.getAttribute('id')}"]`);
    trigger.setAttribute('aria-expanded', false);

    if (!this.querySelector('[aria-expanded="true"]')) {
      this.classList.remove('header--background');
    }

    this.elements.secondaryMenu.classList.remove('header__right--inactive');
    this.megamenuObserver.unobserve(submenu);
    if (this.originalColorScheme) {
      this.returnColorScheme.forEach((el) => {
        el.setAttribute('data-color-scheme', this.originalColorScheme);
      });
      this.returnColorScheme = [];
    }

    const fade = submenu.querySelectorAll('[data-animate-fade]');
    fade.forEach((element) => element.classList.add('faded-out'));
    if (preventFocus === true) return;
    trigger.focus();
  }

  openSubmenu(submenu, focusFirst = false) {
    setTimeout(() => {
      this.timeOpened = Date.now();
      const submenuType = submenu.getAttribute('data-submenu');
      this.setAttribute('open', submenuType);
      const trigger = this.querySelector(`[aria-controls="${submenu.getAttribute('id')}"]`);
      trigger.setAttribute('aria-expanded', true);

      // Move focus to first focusable element when opened via keyboard
      if (focusFirst) {
        const firstFocusable = submenu.querySelector('a, button');
        if (firstFocusable) firstFocusable.focus();
      }
      if (this.getAttribute('data-layout') === 'vertical') {
        const height = Math.max(submenu.clientHeight, this.clientHeight, this.submenuHeight || 0);
        if (submenuType != 'megamenu') {
          this.setSubmenuHeight(height);
        }
        this.megamenuObserver.observe(submenu);
        this.classList.add('header--background');
      }
      if (submenuType === 'megamenu') {
        if (submenu.hasAttribute('data-alt-logo')) {
          this.classList.add('header--alt-logo');
        }
        const style = submenu.getAttribute('data-color-scheme-style');
        if (this.getAttribute('data-layout') === 'horizontal' && style === 'fade') {
          this.originalColorScheme = submenu.getAttribute('data-color-scheme-original');
          const newScheme = submenu.getAttribute('data-color-scheme-transition');
          this.returnColorScheme = [this, submenu];
          this.returnColorScheme.forEach((el) => {
            el.setAttribute('data-color-scheme', newScheme);
          });
        }
        if (this.getAttribute('data-layout') === 'vertical') {
          this.elements.secondaryMenu.classList.add('header__right--inactive');
          this.originalColorScheme = submenu.getAttribute('data-color-scheme-original');
          if (style === 'fade') {
            const newScheme = submenu.getAttribute('data-color-scheme-transition');
            this.returnColorScheme = [this, this.elements.background];
            this.returnColorScheme.forEach((el) => {
              el.setAttribute('data-color-scheme', newScheme);
            });
          }
          this.classList.add('header--background');
        }

        const fade = submenu.querySelectorAll('[data-animate-fade]');
        fade.forEach((element) => element.classList.remove('faded-out'));
      }
    }, 0);
  }

  setSubmenuHeight(height) {
    this.submenuHeight = height;
    document.documentElement.style.setProperty('--submenu-height', height + 'px');
  }

  onSubmenuKeyDown(event) {
    const submenu = event.target.closest('[data-submenu]');
    const focusableElements = submenu.querySelectorAll('a, button:not([disabled])');
    const focusableArray = Array.from(focusableElements);
    const currentIndex = focusableArray.indexOf(document.activeElement);

    switch (event.code.toUpperCase()) {
      case 'ESCAPE':
        this.closeSubmenu(submenu);
        break;
      case 'ARROWDOWN':
        event.preventDefault();
        if (currentIndex < focusableArray.length - 1) {
          focusableArray[currentIndex + 1].focus();
        } else {
          focusableArray[0].focus(); // Wrap to first
        }
        break;
      case 'ARROWUP':
        event.preventDefault();
        if (currentIndex > 0) {
          focusableArray[currentIndex - 1].focus();
        } else {
          focusableArray[focusableArray.length - 1].focus(); // Wrap to last
        }
        break;
      case 'HOME':
        event.preventDefault();
        focusableArray[0]?.focus();
        break;
      case 'END':
        event.preventDefault();
        focusableArray[focusableArray.length - 1]?.focus();
        break;
      case 'TAB':
        // Allow normal tab behavior but keep submenu open while tabbing within it
        break;
    }
  }

  onSubmenuFocusOut(event, submenu) {
    if (event.relatedTarget) {
      // Prevents some double activation issues
      const id = event.relatedTarget.getAttribute('aria-controls');
      if (id) {
        const el = document.getElementById(id);
        if (el.contains(submenu)) {
          return;
        }
      }
    }
    if (!submenu.contains(event.target) || !submenu.contains(event.relatedTarget)) {
      this.closeSubmenu(submenu, true);
    }
  }

  toggleTrigger(event, focusFirst = false) {
    const trigger = event.target;
    const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
    if (trigger.getAttribute('aria-expanded') === 'true') {
      this.closeSubmenu(submenu);
    } else {
      this.openSubmenu(submenu, focusFirst);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-sticky' && newValue !== 'none') {
      window.addEventListener('scroll', this.onScroll.bind(this), false);
    }
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
    if (this.getAttribute('open')) return;

    const transparentEl = document.querySelector('[data-support-transparent-header]');
    if (transparentEl && scrollTop > transparentEl.clientHeight) {
      this.classList.add('header--filled');
    } else if (!transparentEl && scrollTop > 0) {
      this.classList.add('header--filled');
    } else {
      this.classList.remove('header--filled');
    }

    if (this.dataset.sticky === 'scroll') {
      if (scrollTop > this.currentScrollTop && scrollTop > this.clientHeight) {
        this.classList.add('header--scrolled-past');
        this.classList.remove('header--revealed');
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.clientHeight) {
        this.classList.add('header--scrolled-past');
        if (!this.preventReveal) {
          this.classList.add('header--revealed');
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 75);

          this.classList.remove('header--revealed');
        }
      } else if (scrollTop <= this.clientHeight) {
        this.classList.remove('header--scrolled-past');
        this.classList.remove('header--revealed');
      }
    }
    this.currentScrollTop = scrollTop;
  }
}

customElements.define('header-menu', HeaderMenu);
